<template>
  <section>
    <v-container fluid>
      <v-sheet class="overflow-hidden mt-n2 transparent pa-0">
        <v-toolbar color="transparent" class="pa-0" flat>
          <v-col cols="auto" class="py-1 font-weight-bold">
            {{ $t("follow booking") }}
          </v-col>
          <v-spacer />
          <v-btn fab text small color="grey darken-2 mx-1" @click="changeDay(1)">
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
          <v-menu
            v-model="datePickerMenu"
            offset-y
            :close-on-content-click="false"
            transition="scale-transition"
            :nudge-left="$i18n.locale == 'ar' ? '-60' : '60'" nudge-top="-7"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-toolbar-title
                class="px-2 date-title"
                v-bind="attrs"
                v-on="on"
              >
                {{ activeDay | dateAr }}
              </v-toolbar-title>
            </template>

            <v-date-picker
              v-model="tempActiveDay"
              no-title
              scrollable
              :locale="calendarLocale"
              @input="onDatePicked"
            />
          </v-menu>
          <v-btn fab text small color="grey darken-2 mx-1" @click="changeDay(-1)">
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn outlined class="mx-4" color="grey darken-2" @click="setToday">
            {{ $t("today") }}
          </v-btn>
        </v-toolbar>
      </v-sheet>
      <v-row class="fill-height" v-if="!pageData.isLoading">
        <v-col>
          <v-sheet :height="$store.state.Settings.windowSize.y - 150" class="position-relative" :key="calendarKey">

            <template v-if="!is_day_off">
              <div class="first_time">
                {{ formatDisplayTime(times[0]?.time) }}
              </div>
              <v-calendar
                ref="calendar"
                style="height: 100%"
                v-model="focus"
                color="primary"
                type="category"
                category-show-all
                :locale="calendarLocale"
                :interval-minutes="interval_minutes"
                :interval-count="interval_count"
                :interval-height="interval_height"
                :first-time="times[0]?.time"
                :now="today"
                :start="activeDay"
                :categories="barbersIds"
                :events="events"
                :event-color="getEventColor"
                @change="buildCalendarEvents"
                @click:event="openEventDetails"
              >
                <template v-slot:category="{ category }">
                  <v-sheet color="gray11" class="pa-3 rounded-0 position-relative" style="z-index: 300000">
                    <v-row justify="center" align="center">
                      <v-col cols="auto" class="subtitle-2 py-1 font-weight-bold gray3--text" v-lazy-container="{ selector: 'img' }">
                        <img 
                          :height="40" 
                          :width="40" 
                          :data-src="getBarberImage(category)" 
                          class="d-block mi-start-auto rounded-circle" 
                          :alt="category" 
                        />
                      </v-col>
                      <v-col cols="auto" class="text-caption py-1 font-weight-bold gray3--text">
                        {{ getBarberName(category) | capitalize }}
                      </v-col>
                    </v-row>
                  </v-sheet>
                </template>

                <template v-slot:interval="{ category, time }">
                  <v-col cols="12" lg="10" class="ma-auto py-0 d-flex justify-center">
                    <v-hover v-slot="{ hover }">

                      <v-btn
                        v-if="isSlotAvailable(category, time)"
                        depressed
                        small
                        block
                        :height="32"
                        class="d-block rounded-xl mt-2 blue1--text ma-auto position-relative"
                        style="z-index: 20"
                        color="blue12"
                        :disabled="!canBookNow(time)"
                        @click="addBooking(category, time)"
                      >
                        <v-icon left>mdi-plus</v-icon>
                        {{ $t("add") }} {{ $t("from time") }} {{ formatDisplayTime(time) }}
                      </v-btn>

                      <div
                        v-else-if="activeDay < today"
                        class="slot-disabled text-center mt-2 d-flex align-center justify-center ma-auto"
                        :class="{ 'gray9--text': !hover, 'gray9': hover }"
                      >
                        -
                      </div>

                      <div
                        v-else
                        class="slot-disabled text-center mt-2 d-flex align-center justify-center ma-auto"
                        :class="{ 'gray9--text': !hover, 'gray9': hover }"
                      >
                        {{ $t("out of the shift") }}
                      </div>

                    </v-hover>
                  </v-col>
                </template>

                <template v-slot:event="{ event }">
                  <v-col cols="12" lg="12" class="position-relative">
                    <v-row class="text-truncate ma-auto" justify="center" align="center">
                      <v-col cols="6" class="d-flex pa-0 justify-end">
                        <div class="font-weight-bold text-subtitle-1 px-2">
                          {{ event.booking.barber_customer_name }} - {{ event.booking.barber_customer_mobile }}
                        </div>
                      </v-col>
                      <v-col cols="6" class="gray12--text pa-0">
                        <div class="d-flex align-start flex-column">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <div v-bind="attrs" v-on="on">
                                <v-icon color="white" small>mdi-account-wrench</v-icon>
                                {{ $t("services") }}
                              </div>
                            </template>
                            <span class="d-flex" v-for="(service, index) in event.servicesList" :key="index">
                              <v-icon color="gray11" size="7" class="mx-1">mdi-circle</v-icon>
                              {{ service }}
                            </span>
                          </v-tooltip>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </template>
              </v-calendar>
            </template>

            <template v-else>
              <v-col cols="12" class="py-16">
                <v-alert class="my-16 rounded-xl text-center" color="info" outlined>
                  <v-icon color="blue1" size="60">mdi-information</v-icon>
                  <div class="text-h5 mt-5">
                    {{ $t("branch is off this day") | capitalize }}
                  </div>
                </v-alert>
              </v-col>
            </template>

          </v-sheet>
        </v-col>
      </v-row>

      <div class="my-16" v-if="pageData.isLoading">
        <AnimatedLoading :height="100" :isLoading="pageData.isLoading" />
      </div>

      <ShowEventRequestDialog
        :event="selectedEvent"
        :dialog="eventDialog"
        :booking_date="activeDay"
        :backValueMethod="handleDialogClose"
      />
    </v-container>
  </section>
</template>

<script>
import AnimatedLoading from "@/components/AnimatedLoading.vue";
import ShowEventRequestDialog from "./ShowEventRequestDialog";

export default {
  name: "BarberBookingCalendar",
  components: {
    ShowEventRequestDialog,
    AnimatedLoading,
  },

  data: () => ({
    // Page & API configurations
    pageData: {
      isLoading: true,
    },

    // Calendar Configurations
    calendarKey: 1,
    today: null,
    activeDay: null,
    focus: "",
    interval_minutes: 30,
    interval_height: 47,
    interval_count: 0,
    is_day_off: false,

    // Data Storage
    barbersIds: [],
    all_barbers: [],
    rawBookings: [],
    events: [],
    times: [],

    // UI State
    eventDialog: false,
    selectedEvent: {},
    latestFetchRequestId: 0,

    // Date picker
    datePickerMenu: false,
    tempActiveDay: null,
  }),

  computed: {
    calendarLocale() {
      return this.$vuetify.rtl ? 'ar' : 'en-US';
    }
  },

  watch: {
    $route() {
      this.initPageData();
      this.fetchDailyData();
    },
    datePickerMenu(val) {
      if (val) this.tempActiveDay = this.activeDay;
    },
  },

  mounted() {
    this.initPageData();
    this.fetchDailyData();
  },

  methods: {
    // ==========================================
    // 1. Initialization & API Calls
    // ==========================================
    initPageData() {
      this.today = this.$global.GetCurrentDate();
      this.activeDay = this.$route.params.active_date || this.today;
      this.tempActiveDay = this.activeDay;
    },

    async fetchDailyData() {
        
      const requestId = ++this.latestFetchRequestId;
      const requestedDay = this.activeDay;

      this.resetCalendarState();

        try {
          const endpoint = `barber_booking?active_date=${requestedDay}`;
          const response = await this.$api.GET_METHOD(endpoint);

          // Ignore stale responses when user changes day rapidly.
          if (requestId !== this.latestFetchRequestId) return;

          if (response.check && response.data) {
            this.processApiResponse(response.data);
            this.$refs.calendar?.checkChange();
            this.$nextTick(() => this.scrollToCurrentTime());
          }
        } catch (error) {
          console.error("Failed to fetch booking data:", error);
        } finally {
          if (requestId === this.latestFetchRequestId) {
            this.pageData.isLoading = false;
          }
        }
    },

    resetCalendarState() {
      this.pageData.isLoading = true;
      this.is_day_off = false;
      this.calendarKey += 1;
      this.barbersIds = [];
      this.rawBookings = [];
      this.all_barbers = [];
      this.times = [];
    },

    processApiResponse(data) {
      this.is_day_off = data.is_day_off;
      this.all_barbers = data.barbers || [];
      this.times = data.times || [];
      this.interval_minutes = data.interval_minutes || 30;

      this.interval_count = data.interval_count ||
        this.$global.GetTimesSlots(this.times.at(0), this.times.at(-1), this.interval_minutes)?.length;

      // Map Barbers
      this.all_barbers.forEach((barber) => {
        if (barber?.barber_work_times?.length) {
          this.barbersIds.push(String(barber.id));
          barber.beginning_work = barber.barber_work_times[0]?.beginning_work;
          barber.end_work = barber.barber_work_times[0]?.end_work;
        }
      });

      if (data.items) {
        Object.entries(data.items).forEach(([barberId, requests]) => {
          requests.forEach(req => {
            this.rawBookings.push({
              ...req,
              barber: String(barberId),
              start: req.from_time_title,
              end: req.to_time_title,
            });
          });
        });
      }
    },

    // ==========================================
    // 2. Data Processing & Formatting
    // ==========================================
    buildCalendarEvents() {
      const eventsList = [];

      this.rawBookings.forEach((event) => {

        let startTimeStr = event.from_time_title;
        let endTimeStr = event.to_time_title;

        if (!startTimeStr || !endTimeStr) return;

        let startDateTime = new Date(`${this.activeDay} ${startTimeStr}`);
        let endDateTime = new Date(`${this.activeDay} ${endTimeStr}`);

        // Handle overnight bookings
        if (endDateTime <= startDateTime) {
          const nextDay = this.$global.AddToDate(this.activeDay, 1);
          endDateTime = new Date(`${nextDay} ${endTimeStr}`);
        }

        eventsList.push({
          name: event.services_names,
          servicesList: event.services_names ? event.services_names.split(',') : [],
          // Convert to UTC for the calendar to work
          start: this.convertToUTC(startDateTime),
          end: this.convertToUTC(endDateTime),
          color: event.has_invoice === 1 ? "success" : "primary",
          timed: true,
          category: event.barber,
          booking: event, 
        });
      });

      this.events = eventsList;
    },

    convertToUTC(dateObj) {
      return new Date(dateObj.getTime() + dateObj.getTimezoneOffset());
    },

    // ==========================================
    // 3. UI Helpers & Logic
    // ==========================================
    getBarberImage(id) {
      const barber = this.all_barbers.find(b => b.id == id);
      return barber && barber.image ? this.$store.state.endpointURL + barber.image : '';
    },

    getBarberName(id) {
      const barber = this.all_barbers.find(b => b.id == id);
      return barber ? barber.name : '';
    },

    getTimeStringById(timeId) {
      const timeObj = this.times.find(t => t.id === timeId);
      return timeObj ? timeObj.time : null;
    },

    formatDisplayTime(timeStr) {
      if (!timeStr) return "";
      const fullTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
      return new Date(`${this.activeDay} ${fullTime}`).toLocaleTimeString(
        this.calendarLocale,
        { hour: "numeric", minute: "numeric", hour12: true }
      );
    },

    // ==========================================
    // 4. Availability & Business Logic
    // ==========================================
    canBookNow(timeStr) {
      const now = new Date();
      const fullTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
      const targetTime = new Date(`${this.activeDay} ${fullTime}`);
      const diffInMinutes = Math.round((targetTime - now) / 60000);

      return diffInMinutes >= 20;
    },

    isSlotAvailable(barberId, timeStr) {
      if (this.activeDay < this.today) return false;

      const barber = this.all_barbers.find(b => b.id == barberId);
      if (!barber || !barber.barber_work_times) return false;

      const fullTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
      const targetTime = new Date(`${this.activeDay} ${fullTime}`);

      return barber.barber_work_times.some(shift => {
        if (shift.is_day_off === 1 || !shift.beginning_work || !shift.end_work) return false;

        let shiftStart = new Date(`${this.activeDay} ${shift.beginning_work}`);
        let shiftEnd = new Date(`${this.activeDay} ${shift.end_work}`);

        if (shiftEnd <= shiftStart) {
          shiftEnd = new Date(`${this.$global.AddToDate(this.activeDay, 1)} ${shift.end_work}`);
        }

        return targetTime >= shiftStart && targetTime < shiftEnd;
      });
    },

    // ==========================================
    // 5. User Interactions
    // ==========================================
    changeDay(daysToAdd) {
      const newDate = daysToAdd > 0
        ? this.$global.AddToDate(this.activeDay, daysToAdd)
        : this.$global.SubtractFromDate(this.activeDay, Math.abs(daysToAdd));
      this.$router.push(`/barber/barber_booking/${newDate}`);
    },

    setToday() {
      this.$router.push(`/barber/barber_booking/${this.today}`);
    },

    onDatePicked(date) {
      // Avoid pushing when picker closes programmatically
      if (!this.datePickerMenu) return;
      if (!date) return;

      const newDate = String(date);
      if (newDate === String(this.activeDay)) {
        this.datePickerMenu = false;
        return;
      }

      // Keep UI in sync instantly; route watcher will refresh data
      this.activeDay = newDate;
      this.datePickerMenu = false;
      this.$router.push(`/barber/barber_booking/${newDate}`);
    },

    addBooking(barberId, timeStr) {
      const fullTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
      const timeObj = this.times.find(t => t.time === fullTime);

      if (timeObj && this.canBookNow(fullTime)) {
        this.$router.push(
          `/barber/barber_booking-control?barber_booking_date=${this.activeDay}&barber_id=${barberId}&start_time=${timeObj.id}`
        );
      }
    },

    openEventDetails({ event }) {
      this.selectedEvent = event;
      this.eventDialog = true;
    },

    handleDialogClose(status) {
      this.eventDialog = false;
      this.selectedEvent = {};
      if (status) this.fetchDailyData();
    },

    // ==========================================
    // 6. DOM Manipulations
    // ==========================================
    scrollToCurrentTime() {
      const activeDateStr = String(this.activeDay).split(" ")[0];
      const todayDateStr = String(this.today).split(" ")[0];
      if (activeDateStr !== todayDateStr) return;

      setTimeout(() => {
        if (!this.$refs.calendar) return;

        try {
          const calendarEl = this.$refs.calendar.$el;
          const intervalEls = Array.from(calendarEl.querySelectorAll(".v-calendar-daily__interval, .v-calendar-daily__interval-label"));

          if (!intervalEls.length) return;

          const startTimeStr = this.times[0]?.time || "00:00:00";
          const startInMinutes = parseInt(startTimeStr.split(":")[0]) * 60 + parseInt(startTimeStr.split(":")[1]);
          const now = new Date();
          const currentInMinutes = now.getHours() * 60 + now.getMinutes();

          if (currentInMinutes <= startInMinutes) {
            intervalEls[0].scrollIntoView({ block: "start", behavior: "smooth" });
            return;
          }

          const diffMinutes = currentInMinutes - startInMinutes;
          const index = Math.round(diffMinutes / this.interval_minutes);
          const targetEl = intervalEls[Math.max(0, Math.min(index, intervalEls.length - 1))];

          if (targetEl) {
            targetEl.scrollIntoView({ block: "center", behavior: "smooth" });
            return;
          }

          // Fallback for older Vuetify DOM structure
          const scrollArea = calendarEl.querySelector(".v-calendar-daily__scroll-area");
          if (scrollArea) {
            const scrollTarget = diffMinutes * (this.interval_height / this.interval_minutes) - 50;
            if (scrollArea.scrollHeight > scrollArea.clientHeight + 10) {
              scrollArea.scrollTop = scrollTarget;
            } else {
              window.scrollTo({ top: calendarEl.getBoundingClientRect().top + window.pageYOffset + scrollTarget, behavior: "smooth" });
            }
          }
        } catch (error) {
          console.error("Calendar scroll error:", error);
        }
      }, 500);
    },

    getEventColor(event) {
      return event.color;
    },
  },
};
</script>

<style scoped lang="scss">
.date-title {
  cursor: pointer;
  user-select: none;
}

.first_time {
  position: absolute;
  z-index: 56;
  font-size: 10px;
  font-weight: 700;
  color: #424242;
  top: 52px;
  padding-right: 4px;
}

.v-application--is-rtl .first_time {
  right: 10px;
}

.v-application--is-ltr .first_time {
  left: 10px;
}

.slot-disabled {
  width: 100%;
  height: 35px;
}
</style>